import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Stethoscope, Plus, Edit, Archive, ArchiveRestore, Save, X,
    ChevronDown, ChevronRight, Calendar as CalendarIcon,
    LayoutTemplate,
} from 'lucide-react';
import { formatDateTime } from '../../../i18n/locale';
import {
    apiService, Appointment, TreatmentNote, TreatmentNoteKind,
    CreateTreatmentNotePayload, UpdateTreatmentNotePayload,
    TreatmentNoteTemplate, Indication, SupportedLocale, AudioFile,
} from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import { localeFromPatient, pickLocale } from '../../../utils/i18n';
import ToothChart from './ToothChart';
import NewTreatmentNoteDialog from './NewTreatmentNoteDialog';
import TreatmentNoteTemplateEditor, { TemplateEditorSavePayload } from './TreatmentNoteTemplateEditor';
import AudioConsentBadge from './AudioConsentBadge';

interface Props {
    patientId: number;
    appointments: Appointment[];
    patientName?: string;
    patientPreferredLocale?: string | null;
}

const sectionClass = 'rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5 mb-4';
const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const KIND_LABEL_KEY: Record<TreatmentNoteKind, string> = {
    visit: 'notes.kindVisit',
    note: 'notes.kindNote',
};

const fmtDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    return formatDateTime(iso, {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    }) || iso;
};

interface EditorState {
    kind: TreatmentNoteKind;
    body: string;
    appointment_id: number | null;
    tooth_refs: string[];
    chief_complaint: string;
    findings: string;
    treatment_performed: string;
    plan: string;
    showStructured: boolean;
}

const emptyEditor = (): EditorState => ({
    kind: 'visit',
    body: '',
    appointment_id: null,
    tooth_refs: [],
    chief_complaint: '',
    findings: '',
    treatment_performed: '',
    plan: '',
    showStructured: false,
});

const fromNote = (n: TreatmentNote): EditorState => ({
    kind: n.kind,
    body: n.body,
    appointment_id: n.appointment_id,
    tooth_refs: n.tooth_refs || [],
    chief_complaint: n.chief_complaint || '',
    findings: n.findings || '',
    treatment_performed: n.treatment_performed || '',
    plan: n.plan || '',
    showStructured: !!(n.chief_complaint || n.findings || n.treatment_performed || n.plan),
});

const TreatmentNotesSection: React.FC<Props> = ({ patientId, appointments, patientName, patientPreferredLocale }) => {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const { user } = useAuth();
    const [notes, setNotes] = useState<TreatmentNote[]>([]);
    const [loading, setLoading] = useState(true);
    const [showArchived, setShowArchived] = useState(false);

    // editingId: null = nichts offen, 'new' = neues, number = bearbeite existierende
    const [editingId, setEditingId] = useState<number | 'new' | null>(null);
    const [editor, setEditor] = useState<EditorState>(emptyEditor);
    const [submitting, setSubmitting] = useState(false);

    // Phase A.5 — Template-Flow
    const [templates, setTemplates] = useState<TreatmentNoteTemplate[]>([]);
    const [indications, setIndications] = useState<Indication[]>([]);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [templateEditor, setTemplateEditor] = useState<{
        template: TreatmentNoteTemplate;
        existingNote: TreatmentNote | null;
        audios: AudioFile[];
    } | null>(null);
    const [templateSubmitting, setTemplateSubmitting] = useState(false);

    // Phase B — Audio
    const [audioConsentGranted, setAudioConsentGranted] = useState<boolean>(false);
    const [audioByNote, setAudioByNote] = useState<Record<number, AudioFile[]>>({});

    const practitionerName = user?.firstName
        ? `${user.firstName} ${user.lastName || ''}`.trim()
        : (user?.username || 'admin');
    const resolvedPatientName = patientName || t('notes.patientFallback', { id: patientId });
    const resolvedLocale: SupportedLocale = localeFromPatient(patientPreferredLocale);

    const load = async () => {
        try {
            setLoading(true);
            const res = await apiService.listTreatmentNotes(patientId, { includeArchived: showArchived });
            setNotes(res.data.data || []);
        } catch (e: any) {
            addToast(
                e?.response?.data?.message || t('notes.errorLoad'),
                'error',
            );
            setNotes([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId, showArchived]);

    // Templates + Indications einmal laden (bei mount). Falls Templates leer
    // sind, faellt der "Mit Vorlage"-Button stumm aus.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [tplRes, indRes] = await Promise.all([
                    apiService.listTreatmentNoteTemplates(),
                    apiService.listIndications(),
                ]);
                if (cancelled) return;
                setTemplates(tplRes.data.data || []);
                setIndications(indRes.data.data || []);
            } catch {
                // stumm: Phase-A-Freitext-Pfad funktioniert auch ohne Templates.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const fetchAudiosForNote = async (noteId: number): Promise<AudioFile[]> => {
        try {
            const res = await apiService.listAudioForNote(noteId);
            const list = res.data.data || [];
            setAudioByNote(prev => ({ ...prev, [noteId]: list }));
            return list;
        } catch {
            return [];
        }
    };

    const openTemplateEditorForNew = (template: TreatmentNoteTemplate) => {
        setTemplateDialogOpen(false);
        setTemplateEditor({ template, existingNote: null, audios: [] });
    };

    const openTemplateEditorForExisting = async (note: TreatmentNote) => {
        if (!note.template_id) return;
        const audios = await fetchAudiosForNote(note.id);
        const tpl = templates.find(t => t.id === note.template_id);
        if (tpl) {
            setTemplateEditor({ template: tpl, existingNote: note, audios });
            return;
        }
        // Template ist evtl. nicht in der Liste (archiviert) — direkt holen.
        try {
            const res = await apiService.getTreatmentNoteTemplate(note.template_id);
            setTemplateEditor({ template: res.data.data, existingNote: note, audios });
        } catch {
            addToast(t('notes.templateLoadFallback'), 'info');
            startEdit(note);
        }
    };

    const uploadAudioForNote = async (
        noteId: number, blob: Blob, durationSec: number, locale: SupportedLocale,
    ): Promise<AudioFile | null> => {
        try {
            const res = await apiService.uploadAudio(noteId, blob, { locale });
            const af = res.data.data;
            setAudioByNote(prev => ({ ...prev, [noteId]: [af, ...(prev[noteId] || [])] }));
            return af;
        } catch (e: any) {
            addToast(
                e?.response?.data?.message || t('notes.audioUploadError'),
                'error',
            );
            return null;
        }
    };

    const closeTemplateEditor = () => setTemplateEditor(null);

    const saveTemplateEditor = async (payload: TemplateEditorSavePayload): Promise<TreatmentNote | null> => {
        try {
            setTemplateSubmitting(true);
            const apiPayload: CreateTreatmentNotePayload & UpdateTreatmentNotePayload = {
                template_id: payload.template_id,
                section_values: payload.section_values,
                body_overridden: payload.body_overridden,
                locale: payload.locale,
                title: payload.title,
                kind: 'visit',
            };
            if (payload.body_overridden && payload.body !== undefined) {
                apiPayload.body = payload.body;
            }

            let saved: TreatmentNote | null = null;
            if (templateEditor?.existingNote) {
                const res = await apiService.updateTreatmentNote(templateEditor.existingNote.id, apiPayload);
                saved = res.data.data;
                addToast(t('notes.noteUpdated'), 'success');
            } else {
                const res = await apiService.createTreatmentNote(patientId, apiPayload);
                saved = res.data.data;
                addToast(t('notes.noteCreated'), 'success');
            }
            // Editor schliesst nicht sofort: Audio-Upload + Polling laeuft noch
            // im Editor. Reload der Note-Liste erst nach Editor-Close (oder
            // nach kurzem Delay, damit der User die Aktualisierung sieht).
            await load();
            return saved;
        } catch (e: any) {
            addToast(
                e?.response?.data?.message || t('notes.errorSave'),
                'error',
            );
            return null;
        } finally {
            setTemplateSubmitting(false);
        }
    };

    const startNew = () => {
        setEditor(emptyEditor());
        setEditingId('new');
    };

    const startEdit = (n: TreatmentNote) => {
        setEditor(fromNote(n));
        setEditingId(n.id);
    };

    const cancel = () => {
        setEditingId(null);
        setEditor(emptyEditor());
    };

    const save = async () => {
        if (!editor.body.trim()) {
            addToast(t('notes.bodyRequired'), 'error');
            return;
        }
        try {
            setSubmitting(true);
            const payload: CreateTreatmentNotePayload | UpdateTreatmentNotePayload = {
                kind: editor.kind,
                body: editor.body.trim(),
                appointment_id: editor.appointment_id,
                tooth_refs: editor.tooth_refs,
                chief_complaint: editor.chief_complaint,
                findings: editor.findings,
                treatment_performed: editor.treatment_performed,
                plan: editor.plan,
            };

            if (editingId === 'new') {
                await apiService.createTreatmentNote(patientId, payload as CreateTreatmentNotePayload);
                addToast(t('notes.noteSaved'), 'success');
            } else if (typeof editingId === 'number') {
                await apiService.updateTreatmentNote(editingId, payload);
                addToast(t('notes.noteUpdated'), 'success');
            }
            cancel();
            await load();
        } catch (e: any) {
            addToast(
                e?.response?.data?.message || t('notes.errorSave'),
                'error',
            );
        } finally {
            setSubmitting(false);
        }
    };

    const archive = async (note: TreatmentNote) => {
        if (!window.confirm(t('notes.confirmArchive', { date: fmtDateTime(note.created_at) }))) return;
        try {
            await apiService.archiveTreatmentNote(note.id);
            addToast(t('notes.noteArchived'), 'success');
            await load();
        } catch (e: any) {
            addToast(
                e?.response?.data?.message || t('notes.errorArchive'),
                'error',
            );
        }
    };

    const unarchive = async (note: TreatmentNote) => {
        try {
            await apiService.unarchiveTreatmentNote(note.id);
            addToast(t('notes.noteUnarchived'), 'success');
            await load();
        } catch (e: any) {
            addToast(
                e?.response?.data?.message || t('notes.errorUnarchive'),
                'error',
            );
        }
    };

    const apptLabel = (a: Appointment) =>
        `${a.appointment_date} ${a.start_time} · ${(a as any).procedure_name || t('notes.noAppointment')}`;

    const Editor = (
        <div className="rounded-lg border border-brand-primary/30 bg-brand-surface-2 p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>{t('notes.type')}</label>
                    <select
                        className={inputClass}
                        value={editor.kind}
                        onChange={e => setEditor(s => ({ ...s, kind: e.target.value as TreatmentNoteKind }))}
                    >
                        <option value="visit">{t('notes.kindVisit')}</option>
                        <option value="note">{t('notes.kindNote')}</option>
                    </select>
                </div>
                <div>
                    <label className={labelClass}>{t('notes.linkedAppointment')}</label>
                    <select
                        className={inputClass}
                        value={editor.appointment_id ?? ''}
                        onChange={e => setEditor(s => ({
                            ...s,
                            appointment_id: e.target.value === '' ? null : parseInt(e.target.value),
                        }))}
                    >
                        <option value="">{t('notes.noAppointmentOption')}</option>
                        {appointments.map(a => (
                            <option key={a.id} value={a.id}>{apptLabel(a)}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div>
                <label className={labelClass}>{t('notes.noteBody')}</label>
                <textarea
                    className={`${inputClass} min-h-[120px] resize-y`}
                    placeholder={t('notes.noteBodyPlaceholder')}
                    value={editor.body}
                    onChange={e => setEditor(s => ({ ...s, body: e.target.value }))}
                />
            </div>

            <div>
                <label className={labelClass}>{t('notes.affectedTeeth')}</label>
                <ToothChart
                    value={editor.tooth_refs}
                    onChange={refs => setEditor(s => ({ ...s, tooth_refs: refs }))}
                />
                {editor.tooth_refs.length > 0 && (
                    <div className="mt-2 text-xs text-brand-text-muted">
                        {t('notes.selected', { teeth: editor.tooth_refs.join(', ') })}
                    </div>
                )}
            </div>

            <div>
                <button
                    type="button"
                    onClick={() => setEditor(s => ({ ...s, showStructured: !s.showStructured }))}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-text-muted hover:text-brand-text transition-colors"
                >
                    {editor.showStructured
                        ? <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                        : <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} />}
                    {editor.showStructured ? t('notes.hideStructured') : t('notes.showStructured')}
                </button>
                {editor.showStructured && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className={labelClass}>{t('notes.chiefComplaint')}</label>
                            <input className={inputClass}
                                value={editor.chief_complaint}
                                onChange={e => setEditor(s => ({ ...s, chief_complaint: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>{t('notes.findings')}</label>
                            <input className={inputClass}
                                value={editor.findings}
                                onChange={e => setEditor(s => ({ ...s, findings: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>{t('notes.treatmentPerformed')}</label>
                            <input className={inputClass}
                                value={editor.treatment_performed}
                                onChange={e => setEditor(s => ({ ...s, treatment_performed: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>{t('notes.plan')}</label>
                            <input className={inputClass}
                                value={editor.plan}
                                onChange={e => setEditor(s => ({ ...s, plan: e.target.value }))}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
                <button
                    type="button"
                    onClick={cancel}
                    disabled={submitting}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:text-brand-text border border-brand-border rounded transition-colors cursor-pointer"
                >
                    <X className="h-3.5 w-3.5" strokeWidth={1.75} /> {t('notes.cancel')}
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={submitting}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded transition-colors cursor-pointer disabled:opacity-60"
                >
                    <Save className="h-3.5 w-3.5" strokeWidth={1.75} />
                    {submitting ? t('notes.saving') : t('notes.save')}
                </button>
            </div>
        </div>
    );

    const renderNote = (n: TreatmentNote) => {
        const appt = appointments.find(a => a.id === n.appointment_id) || null;
        const isEditing = editingId === n.id;
        return (
            <li key={n.id} className={`py-3 ${n.archived ? 'opacity-60' : ''}`}>
                {isEditing ? Editor : (
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap text-xs text-brand-text-muted mb-1">
                                <span className="font-medium uppercase tracking-wider">
                                    {t(KIND_LABEL_KEY[n.kind])}
                                </span>
                                <span>·</span>
                                <span className="tabular-nums">{fmtDateTime(n.created_at)}</span>
                                {n.author_username && (<><span>·</span><span>{n.author_username}</span></>)}
                                {n.template_id && (
                                    <>
                                        <span>·</span>
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary">
                                            <LayoutTemplate className="h-3 w-3" strokeWidth={1.75} />
                                            {pickLocale(templates.find(tpl => tpl.id === n.template_id)?.name, resolvedLocale) || t('notes.templateBadgeFallback')}
                                            {n.body_overridden && <span className="text-[10px] opacity-70">· {t('notes.templateEdited')}</span>}
                                        </span>
                                    </>
                                )}
                                {appt && (
                                    <>
                                        <span>·</span>
                                        <span className="inline-flex items-center gap-1">
                                            <CalendarIcon className="h-3 w-3" strokeWidth={1.75} />
                                            {appt.appointment_date} {appt.start_time}
                                        </span>
                                    </>
                                )}
                                {n.archived && <span className="px-1.5 py-0.5 rounded bg-brand-border/40 text-brand-text-muted">{t('notes.archivedBadge')}</span>}
                            </div>
                            <div className="text-sm text-brand-text whitespace-pre-wrap">{n.body}</div>
                            {n.tooth_refs && n.tooth_refs.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {n.tooth_refs.map(ref => (
                                        <span
                                            key={ref}
                                            className="px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary text-xs font-medium tabular-nums"
                                        >
                                            {ref}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {(n.chief_complaint || n.findings || n.treatment_performed || n.plan) && (
                                <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    {n.chief_complaint && (<><dt className="text-brand-text-muted">{t('notes.chiefComplaint')}</dt><dd className="text-brand-text">{n.chief_complaint}</dd></>)}
                                    {n.findings && (<><dt className="text-brand-text-muted">{t('notes.findings')}</dt><dd className="text-brand-text">{n.findings}</dd></>)}
                                    {n.treatment_performed && (<><dt className="text-brand-text-muted">{t('notes.treatmentPerformedShort')}</dt><dd className="text-brand-text">{n.treatment_performed}</dd></>)}
                                    {n.plan && (<><dt className="text-brand-text-muted">{t('notes.planShort')}</dt><dd className="text-brand-text">{n.plan}</dd></>)}
                                </dl>
                            )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            {!n.archived && (
                                <button
                                    type="button"
                                    onClick={() => n.template_id ? openTemplateEditorForExisting(n) : startEdit(n)}
                                    className="p-1.5 text-brand-text-muted hover:text-brand-primary hover:bg-brand-surface-2 rounded transition-colors cursor-pointer"
                                    title={t('notes.edit')}
                                >
                                    <Edit className="h-3.5 w-3.5" strokeWidth={1.75} />
                                </button>
                            )}
                            {n.archived ? (
                                <button
                                    type="button"
                                    onClick={() => unarchive(n)}
                                    className="p-1.5 text-brand-text-muted hover:text-brand-primary hover:bg-brand-surface-2 rounded transition-colors cursor-pointer"
                                    title={t('notes.unarchive')}
                                >
                                    <ArchiveRestore className="h-3.5 w-3.5" strokeWidth={1.75} />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => archive(n)}
                                    className="p-1.5 text-brand-text-muted hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                    title={t('notes.archive')}
                                >
                                    <Archive className="h-3.5 w-3.5" strokeWidth={1.75} />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </li>
        );
    };

    return (
        <div className={sectionClass}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-brand-text flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
                    {t('notes.heading', { count: notes.filter(n => !n.archived).length })}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                    <AudioConsentBadge
                        patientId={patientId}
                        onChange={(g) => setAudioConsentGranted(g === true)}
                    />
                    <label className="inline-flex items-center gap-1 text-xs text-brand-text-muted cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={e => setShowArchived(e.target.checked)}
                            className="rounded border-brand-border cursor-pointer"
                        />
                        {t('notes.showArchived')}
                    </label>
                    {editingId !== 'new' && (
                        <>
                            {templates.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setTemplateDialogOpen(true)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20 border border-brand-primary/30 rounded transition-colors cursor-pointer"
                                    title={t('notes.withTemplateTitle')}
                                >
                                    <LayoutTemplate className="h-3 w-3" strokeWidth={2} />
                                    {t('notes.withTemplate')}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={startNew}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded transition-colors cursor-pointer"
                            >
                                <Plus className="h-3 w-3" strokeWidth={2} />
                                {t('notes.freetext')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {editingId === 'new' && <div className="mb-3">{Editor}</div>}

            {loading ? (
                <p className="text-sm text-brand-text-muted">{t('notes.loading')}</p>
            ) : notes.length === 0 ? (
                <p className="text-sm text-brand-text-muted">{t('notes.empty')}</p>
            ) : (
                <ul className="divide-y divide-brand-border">
                    {notes.map(renderNote)}
                </ul>
            )}

            <NewTreatmentNoteDialog
                open={templateDialogOpen}
                templates={templates}
                practitionerName={practitionerName}
                patientName={resolvedPatientName}
                locale={resolvedLocale}
                onSelect={openTemplateEditorForNew}
                onSelectFreetext={() => { setTemplateDialogOpen(false); startNew(); }}
                onClose={() => setTemplateDialogOpen(false)}
            />

            {templateEditor && (
                <div className="fixed inset-0 z-50 bg-brand-bg">
                    <TreatmentNoteTemplateEditor
                        template={templateEditor.template}
                        indications={indications}
                        practitionerName={practitionerName}
                        patientName={resolvedPatientName}
                        patientPreferredLocale={patientPreferredLocale}
                        existingNote={templateEditor.existingNote}
                        submitting={templateSubmitting}
                        onSave={saveTemplateEditor}
                        onCancel={closeTemplateEditor}
                        audio={{
                            consentGranted: audioConsentGranted,
                            existingAudios: templateEditor.audios,
                            reloadAudios: () => fetchAudiosForNote(
                                templateEditor.existingNote?.id || 0,
                            ),
                            onUpload: uploadAudioForNote,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default TreatmentNotesSection;
